export interface LocationPoint {
  displayName: string
  lat: number
  lon: number
  type: string
}

export interface Venue {
  id: string
  name: string
  type: string
  lat: number
  lon: number
  address?: string
  website?: string
  wheelchair?: 'yes' | 'limited' | 'no'
}

export interface RankedVenue extends Venue {
  minutesA: number
  minutesB: number
  difference: number
  score: number
}

export function walkingMinutes(
  from: Pick<LocationPoint, 'lat' | 'lon'>,
  to: Pick<Venue, 'lat' | 'lon'>,
): number {
  const distanceKm = distanceKilometres(from, to)
  return Math.max(1, Math.round((distanceKm * 1.2 / 4.8) * 60))
}

export function distanceKilometres(
  from: Pick<LocationPoint, 'lat' | 'lon'>,
  to: Pick<LocationPoint, 'lat' | 'lon'>,
): number {
  const earthRadiusKm = 6_371
  const toRadians = (degrees: number) => degrees * Math.PI / 180
  const latitudeDelta = toRadians(to.lat - from.lat)
  const longitudeDelta = toRadians(to.lon - from.lon)
  const value = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat))
    * Math.sin(longitudeDelta / 2) ** 2

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value))
}

export function searchCenters(
  scheduled: Array<{ lat: number; lon: number }>,
  geographic: { lat: number; lon: number },
  includeGeographic: boolean,
): Array<{ lat: number; lon: number }> {
  const candidates = includeGeographic ? [geographic, ...scheduled] : scheduled.length ? scheduled : [geographic]
  const selected: Array<{ lat: number; lon: number }> = []
  for (const candidate of candidates) {
    if (selected.every((center) => distanceKilometres(center, candidate) >= 0.2)) selected.push(candidate)
    if (selected.length === 3) break
  }
  return selected
}

export function boundedVenueCandidates(
  venues: Venue[],
  centers: Array<{ lat: number; lon: number }>,
  maximum = 25,
  priorities: Venue[] = [],
): Venue[] {
  const queues = centers.map((center) => [...venues].sort((left, right) =>
    distanceKilometres(left, center) - distanceKilometres(right, center)))
  const selected: Venue[] = []
  const ids = new Set<string>()
  const venuesById = new Map(venues.map((venue) => [venue.id, venue]))
  for (const priority of priorities.slice(0, Math.min(3, maximum))) {
    const venue = venuesById.get(priority.id)
    if (!venue || ids.has(venue.id)) continue
    selected.push(venue)
    ids.add(venue.id)
  }
  while (selected.length < maximum && ids.size < venues.length) {
    let added = false
    for (const queue of queues) {
      const venue = queue.find((candidate) => !ids.has(candidate.id))
      if (!venue) continue
      selected.push(venue)
      ids.add(venue.id)
      added = true
      if (selected.length === maximum) break
    }
    if (!added) break
  }
  return selected
}

export function rankVenues(venues: Venue[], a: LocationPoint, b: LocationPoint): RankedVenue[] {
  return venues.map((venue) => {
    const minutesA = walkingMinutes(a, venue)
    const minutesB = walkingMinutes(b, venue)
    const difference = Math.abs(minutesA - minutesB)
    return {
      ...venue,
      minutesA,
      minutesB,
      difference,
      score: difference + 0.2 * (minutesA + minutesB),
    }
  }).sort((left, right) => left.score - right.score)
}
